/**
 * UserQuestionWidget — renders AskUserQuestion as a multi-question form.
 *
 * Each question shows its options as selectable cards. Users pick one option
 * per question (or multiple if multiSelect), optionally type in "Other",
 * then submit all answers at once.
 */

import { useState } from "react";
import { useChatStore } from "@/stores/chatStore";
import { Button } from "@/components/common/Button";

interface QuestionOption {
  label: string;
  description: string;
}

interface Question {
  question: string;
  header: string;
  multiSelect: boolean;
  options: QuestionOption[];
}

export function UserQuestionWidget() {
  const pendingQuestions = useChatStore((s) => s.pendingQuestions);
  const answerQuestions = useChatStore((s) => s.answerQuestions);
  const dismissQuestions = useChatStore((s) => s.dismissQuestions);

  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});

  if (!pendingQuestions || pendingQuestions.length === 0) return null;

  const toggleOption = (question: string, label: string, multiSelect: boolean) => {
    setSelections((prev) => {
      const current = prev[question] || [];
      if (multiSelect) {
        return {
          ...prev,
          [question]: current.includes(label)
            ? current.filter((l) => l !== label)
            : [...current, label],
        };
      }
      return { ...prev, [question]: current[0] === label ? [] : [label] };
    });
  };

  const handleSubmit = () => {
    const answers: Record<string, string> = {};
    for (const q of pendingQuestions) {
      const selected = selections[q.question] || [];
      const other = otherTexts[q.question]?.trim();
      if (other) {
        answers[q.question] = [...selected, `Other: ${other}`].join(", ");
      } else if (selected.length > 0) {
        answers[q.question] = selected.join(", ");
      }
    }
    answerQuestions(answers);
  };

  const hasAnyAnswer = pendingQuestions.some(
    (q) => (selections[q.question]?.length ?? 0) > 0 || otherTexts[q.question]?.trim(),
  );

  return (
    <div className="flex justify-start animate-[fadeInUp_300ms_ease-out]">
      <div className="max-w-[85%] w-full rounded-2xl border border-jove-60/30 bg-gradient-to-br from-jove-10 to-surface px-5 py-4 shadow-sm space-y-5">
        {pendingQuestions.map((q: Question) => (
          <div key={q.question}>
            {/* Header chip + question */}
            <div className="flex items-start gap-2 mb-3">
              <span className="shrink-0 px-2 py-0.5 rounded-md bg-jove-30 text-jove text-[10px] font-bold uppercase tracking-wider mt-0.5">
                {q.header}
              </span>
              <p className="text-sm font-medium text-space leading-snug">{q.question}</p>
            </div>

            {/* Options */}
            <div className="space-y-1.5 ml-0.5">
              {q.options.map((opt: QuestionOption) => {
                const isSelected = (selections[q.question] || []).includes(opt.label);
                return (
                  <button
                    key={opt.label}
                    onClick={() => toggleOption(q.question, opt.label, q.multiSelect)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all duration-150
                      ${isSelected
                        ? "border-jove bg-jove-30/50 shadow-sm"
                        : "border-space-30 bg-surface hover:border-jove-60/50 hover:bg-jove-10/30"
                      }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors
                        ${isSelected ? "border-jove bg-jove" : "border-space-40"}`}
                      >
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isSelected ? "text-jove" : "text-space"}`}>
                          {opt.label}
                        </p>
                        <p className="text-xs text-space-40 mt-0.5">{opt.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Other text input */}
              <div className="mt-2">
                <input
                  type="text"
                  placeholder="Other — type your answer..."
                  value={otherTexts[q.question] || ""}
                  onChange={(e) => setOtherTexts((prev) => ({ ...prev, [q.question]: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-xl border border-space-30 bg-surface
                             focus:border-jove-60 focus:ring-2 focus:ring-jove-60/20 focus:outline-none
                             placeholder:text-space-40 transition-all"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-2 border-t border-space-30/50">
          <Button variant="ghost" size="sm" onClick={dismissQuestions}>
            Skip
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!hasAnyAnswer}>
            Submit answers
          </Button>
        </div>
      </div>
    </div>
  );
}
